/**
 * City Service
 * Fetches cities from static JSON file (same as mobile app)
 */

import citiesData from "@/lib/data/cities.json";

export interface City {
  id: string;
  name: string;
  nameAr?: string;
  country: string;
  countryCode: string;
  latitude?: number;
  longitude?: number;
  isCapital?: boolean;
  population?: number;
}

interface CitiesData {
  countries: {
    [countryCode: string]: {
      name: string;
      cities: City[];
    };
  };
}

let cachedCities: CitiesData | null = null;

/**
 * Load cities from static JSON file
 */
function loadCitiesFromFile(): CitiesData {
  try {
    return citiesData as CitiesData;
  } catch (error) {
    console.error("Error loading cities from static file:", error);
    throw error;
  }
}

/**
 * Get all cities for a specific country
 */
export function getCitiesByCountry(countryCode: string): City[] {
  const data = loadCitiesFromFile();
  const countryData = data.countries[countryCode.toUpperCase()];
  
  if (!countryData) {
    console.warn(`No cities found for country: ${countryCode}`);
    return [];
  }
  
  return countryData.cities || [];
}

/**
 * Get major cities (capitals and large cities) for a specific country
 */
export function getMajorCitiesByCountry(countryCode: string): City[] {
  const cities = getCitiesByCountry(countryCode);
  
  // Filter major cities (capitals or population > 200,000)
  return cities
    .filter(city => city.isCapital || (city.population && city.population > 200000))
    .sort((a, b) => (b.population || 0) - (a.population || 0));
}

/**
 * Search cities by name
 */
export function searchCities(query: string, countryCode?: string): City[] {
  const data = loadCitiesFromFile();
  const queryLower = query.toLowerCase().trim();
  
  if (!queryLower) return [];
  
  const results: City[] = [];
  
  if (countryCode) {
    // Search within specific country
    const cities = getCitiesByCountry(countryCode);
    for (const city of cities) {
      if (
        city.name.toLowerCase().includes(queryLower) ||
        city.nameAr?.toLowerCase().includes(queryLower)
      ) {
        results.push(city);
      }
    }
  } else {
    // Search across all countries
    for (const countryEntry of Object.entries(data.countries)) {
      const cities = countryEntry[1].cities || [];
      for (const city of cities) {
        if (
          city.name.toLowerCase().includes(queryLower) ||
          city.nameAr?.toLowerCase().includes(queryLower)
        ) {
          results.push(city);
        }
      }
    }
  }
  
  // Sort by population (larger cities first)
  results.sort((a, b) => (b.population || 0) - (a.population || 0));
  
  return results;
}

/**
 * Get city by name (case-insensitive)
 */
export function getCityByName(cities: City[], name: string): City | undefined {
  return cities.find(
    (city) => city.name.toLowerCase() === name.toLowerCase()
  );
}






